import { useState, useMemo, useEffect } from "react";

const GOLD     = "#C9A96E";
const INK      = "#1C1915";
const INK_SOFT = "#4A4540";
const BORDER   = "#E7E1D8";
const SOFT_BG  = "#F7F5F2";
const WHITE    = "#FFFFFF";

const fmt = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n) + " MAD";
const pct = (n) => (isNaN(n) || !isFinite(n) ? "—" : n.toFixed(1) + "%");
const diagColor = (marge) => {
  if (marge >= 20) return { color: "#1A7A44", bg: "#E8F6EF", label: "Excellent" };
  if (marge >= 12) return { color: "#2E6DB4", bg: "#EEF4FD", label: "Bon" };
  if (marge >= 6)  return { color: GOLD,      bg: "#FDF9F2", label: "Correct" };
  return { color: "#C0392B", bg: "#FDEDEC", label: "Faible" };
};

const inputStyle = {
  width: "100%", padding: "8px 10px",
  border: `1px solid ${BORDER}`, borderRadius: 6,
  fontSize: 13, color: INK, background: "#FAFAF8",
  boxSizing: "border-box", outline: "none",
};
const labelStyle = {
  display: "block", fontSize: 10, letterSpacing: "0.12em",
  textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600,
};
const sectionTitle = {
  fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase",
  color: GOLD, marginBottom: 14, fontWeight: 700,
};

const TYPE_PROJETS = [
  { value: "villa",       label: "Villa individuelle",      forfait: 120000 },
  { value: "lotissement", label: "Lotissement / Promotion", forfait: 350000 },
  { value: "renovation",  label: "Rénovation",              forfait: 80000  },
  { value: "riad",        label: "Riad / Bien atypique",    forfait: 100000 },
];

const SCENARIOS_DEFAULT = [
  { id: "prudent",   label: "Prudent",   pctVente: -10, pctCout: 10 },
  { id: "realiste",  label: "Réaliste",  pctVente: 0,   pctCout: 0  },
  { id: "ambitieux", label: "Ambitieux", pctVente: 10,  pctCout: -5 },
];

const DEVISES = [
  { code: "MAD", label: "Dirham marocain", symbol: "MAD", taux: 1       },
  { code: "EUR", label: "Euro",            symbol: "€",   taux: 0.092   },
  { code: "USD", label: "Dollar US",       symbol: "$",   taux: 0.099   },
  { code: "GBP", label: "Livre sterling",  symbol: "£",   taux: 0.079   },
  { code: "CHF", label: "Franc suisse",    symbol: "CHF", taux: 0.089   },
  { code: "CAD", label: "Dollar canadien", symbol: "CA$", taux: 0.136   },
  { code: "AED", label: "Dirham EAU",      symbol: "AED", taux: 0.364   },
  { code: "SAR", label: "Riyal saoudien",  symbol: "SAR", taux: 0.372   },
];

let nextId = 1;
const mkVilla = () => ({ id: nextId++, type: "", quantite: "", prixUnitaire: "", coutConstruction: "" });

const printStyles = `
  @media print {
    body * { visibility: hidden !important; }
    #print-zone, #print-zone * { visibility: visible !important; }
    #print-zone { position: absolute; left: 0; top: 0; width: 100%; }
    .no-print { display: none !important; }
    @page { margin: 20mm; }
  }
`;

function calcFraisEtudes(surface, etages, typeStructure) {
  const s = parseFloat(surface) || 0;
  const e = parseInt(etages) || 1;
  if (s === 0) return null;
  const isImmeuble = typeStructure === "immeuble";
  const archMin = isImmeuble ? s * 100 : Math.max(25000, s * 200 * 0.04);
  const archMax = isImmeuble ? s * 200 : Math.max(80000, s * 200 * 0.07);
  const betMin = isImmeuble ? 10000 + e * 3000 : 1500;
  const betMax = isImmeuble ? 30000 + e * 2000 : 2500;
  const topoMin = isImmeuble ? 15000 : 3000;
  const topoMax = isImmeuble ? 25000 : 15000;
  const controlMin = isImmeuble ? 15000 : 0;
  const controlMax = isImmeuble ? 35000 : 15000;
  const tauxPermis = typeStructure === "villa" ? 30 : typeStructure === "lotissement" ? 25 : 20;
  const taxePermis = s * e * tauxPermis;
  const coutEstime = s * 4000;
  const trcMin = coutEstime * 0.002;
  const trcMax = coutEstime * 0.005;
  const totalMin = archMin + betMin + topoMin + controlMin + taxePermis + trcMin;
  const totalMax = archMax + betMax + topoMax + controlMax + taxePermis + trcMax;
  return {
    lignes: [
      { poste: "Architecte (conception + suivi)",     min: archMin,    max: archMax    },
      { poste: "Bureau d'études techniques (BET)",    min: betMin,     max: betMax     },
      { poste: "Topographe + Étude de sol",           min: topoMin,    max: topoMax    },
      { poste: "Bureau de contrôle",                  min: controlMin, max: controlMax },
      { poste: `Taxe permis (${tauxPermis} MAD/m²)`,  min: taxePermis, max: taxePermis },
      { poste: "Assurance TRC (0.2% – 0.5% travaux)", min: trcMin,     max: trcMax     },
    ],
    totalMin, totalMax, moyenne: (totalMin + totalMax) / 2,
  };
}

export default function ScenarioSimulationPage() {
  const STORAGE_KEY = "yoma_scenario_simulation_page_v1";
  const [nomProjet,       setNomProjet]       = useState("");
  const [typeProjet,      setTypeProjet]      = useState("lotissement");
  const [villas,          setVillas]          = useState([mkVilla()]);
  const [coutTerrain,     setCoutTerrain]     = useState("");
  const [fraisNotairePct, setFraisNotairePct] = useState("7");
  const [fraisDivers,     setFraisDivers]     = useState("");
  const [forfaitYoma,     setForfaitYoma]     = useState("350000");
  const [inclureYoma,     setInclureYoma]     = useState(true);
  const [afficherYoma,    setAfficherYoma]    = useState(true);
  const [scenarios,       setScenarios]       = useState(SCENARIOS_DEFAULT);
  const [printMode,       setPrintMode]       = useState(null);
  const [deviseCode,      setDeviseCode]      = useState("MAD");
  const [tauxManuel,      setTauxManuel]      = useState("");
  const [surfaceEtudes,   setSurfaceEtudes]   = useState("");
  const [nbrEtages,       setNbrEtages]       = useState("1");
  const [typeStructure,   setTypeStructure]   = useState("villa");
  const [afficherEtudes,  setAfficherEtudes]  = useState(false);
  const [hydrated,        setHydrated]        = useState(false);

  // ── Persistance locale ───────────────────────────────────────────────────
  // Lecture au chargement
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setHydrated(true);
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed.nomProjet != null)       setNomProjet(parsed.nomProjet);
      if (parsed.typeProjet != null)      setTypeProjet(parsed.typeProjet);
      if (Array.isArray(parsed.villas) && parsed.villas.length > 0) {
        setVillas(parsed.villas);
        const maxId = parsed.villas.reduce((m, v) => typeof v.id === "number" && v.id > m ? v.id : m, 0);
        if (maxId > 0) nextId = maxId + 1;
      }
      if (parsed.coutTerrain != null)     setCoutTerrain(parsed.coutTerrain);
      if (parsed.fraisNotairePct != null) setFraisNotairePct(parsed.fraisNotairePct);
      if (parsed.fraisDivers != null)     setFraisDivers(parsed.fraisDivers);
      if (parsed.forfaitYoma != null)     setForfaitYoma(parsed.forfaitYoma);
      if (typeof parsed.inclureYoma === "boolean")  setInclureYoma(parsed.inclureYoma);
      if (typeof parsed.afficherYoma === "boolean") setAfficherYoma(parsed.afficherYoma);
      if (Array.isArray(parsed.scenarios) && parsed.scenarios.length > 0) {
        setScenarios(parsed.scenarios);
      }
      if (parsed.deviseCode != null)      setDeviseCode(parsed.deviseCode);
      if (parsed.tauxManuel != null)      setTauxManuel(parsed.tauxManuel);
      if (parsed.surfaceEtudes != null)   setSurfaceEtudes(parsed.surfaceEtudes);
      if (parsed.nbrEtages != null)       setNbrEtages(parsed.nbrEtages);
      if (parsed.typeStructure != null)   setTypeStructure(parsed.typeStructure);
      if (typeof parsed.afficherEtudes === "boolean") setAfficherEtudes(parsed.afficherEtudes);
    } catch (_) {
      // ignore JSON / accès
    }
    setHydrated(true);
  }, []);

  // Écriture à chaque modification après hydratation
  useEffect(() => {
    if (!hydrated) return;
    try {
      const payload = {
        nomProjet,
        typeProjet,
        villas,
        coutTerrain,
        fraisNotairePct,
        fraisDivers,
        forfaitYoma,
        inclureYoma,
        afficherYoma,
        scenarios,
        deviseCode,
        tauxManuel,
        surfaceEtudes,
        nbrEtages,
        typeStructure,
        afficherEtudes,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) {
      // ignore erreurs d'écriture
    }
  }, [
    hydrated,
    nomProjet,
    typeProjet,
    villas,
    coutTerrain,
    fraisNotairePct,
    fraisDivers,
    forfaitYoma,
    inclureYoma,
    afficherYoma,
    scenarios,
    deviseCode,
    tauxManuel,
    surfaceEtudes,
    nbrEtages,
    typeStructure,
    afficherEtudes,
  ]);

  // ── Devise ────────────────────────────────────────────────────────────────
  const deviseActive = DEVISES.find(d => d.code === deviseCode) || DEVISES[0];
  const tauxFinal    = parseFloat(tauxManuel) || deviseActive.taux;

  function fmtDev(n) {
    if (deviseCode === "MAD") return fmt(n);
    const converted = n * tauxFinal;
    return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(converted) + " " + deviseActive.symbol;
  }

  function fmtBoth(n) {
    if (deviseCode === "MAD") return fmt(n);
    return (
      <span><span style={{ fontWeight: 700 }}>{fmtDev(n)}</span><span style={{ fontSize: 11, color: "#999", marginLeft: 4 }}>({fmt(n)})</span></span>
    );
  }

  function handleTypeProjet(val) {
    setTypeProjet(val);
    const found = TYPE_PROJETS.find(t => t.value === val);
    if (found) setForfaitYoma(String(found.forfait));
  }

  function addVilla() { setVillas(v => [...v, mkVilla()]); }
  function removeVilla(id) { setVillas(v => v.filter(x => x.id !== id)); }
  function updateVilla(id, field, val) {
    setVillas(v => v.map(x => x.id === id ? { ...x, [field]: val } : x));
  }
  function updateScenario(id, field, val) {
    setScenarios(s => s.map(x => x.id === id ? { ...x, [field]: parseFloat(val) || 0 } : x));
  }

  function handlePrint(mode) {
    setPrintMode(mode);
    setTimeout(() => { window.print(); setPrintMode(null); }, 300);
  }

  const fraisEtudes = useMemo(() =>
    calcFraisEtudes(surfaceEtudes, nbrEtages, typeStructure),
    [surfaceEtudes, nbrEtages, typeStructure]
  );

  const computed = useMemo(() => {
    const terrain    = parseFloat(coutTerrain)    || 0;
    const divers     = parseFloat(fraisDivers)    || 0;
    const honoraires = (inclureYoma ? parseFloat(forfaitYoma) : 0) || 0;
    const notairePct = parseFloat(fraisNotairePct)|| 0;
    const villasParsed = villas.map(v => ({
      ...v,
      qty:  parseInt(v.quantite)           || 0,
      prix: parseFloat(v.prixUnitaire)     || 0,
      cout: parseFloat(v.coutConstruction) || 0,
    }));
    const caBase         = villasParsed.reduce((s, v) => s + v.qty * v.prix, 0);
    const coutConstrBase = villasParsed.reduce((s, v) => s + v.qty * v.cout, 0);
    return scenarios.map(sc => {
      const ca           = caBase * (1 + sc.pctVente / 100);
      const coutConstr   = coutConstrBase * (1 + sc.pctCout / 100);
      const fraisNotaire = ca * (notairePct / 100);
      const coutTotal    = coutConstr + terrain + divers + honoraires + fraisNotaire;
      const benefice     = ca - coutTotal;
      const marge        = ca > 0 ? (benefice / ca) * 100 : 0;
      const parType = villasParsed.map(v => {
        const caV    = v.qty * v.prix * (1 + sc.pctVente / 100);
        const coutV  = v.qty * v.cout * (1 + sc.pctCout / 100);
        const benV   = caV - coutV;
        const margeV = caV > 0 ? (benV / caV) * 100 : 0;
        return { ...v, caV, coutV, benV, margeV };
      });
      return { ...sc, ca, coutConstr, fraisNotaire, coutTotal, benefice, marge, parType, honoraires };
    });
  }, [villas, coutTerrain, fraisDivers, forfaitYoma, fraisNotairePct, scenarios, inclureYoma]);

  const totalVillas = villas.reduce((s, v) => s + (parseInt(v.quantite) || 0), 0);
  const today = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  const PrintZone = () => (
    <div id="print-zone" style={{ display: "none", fontFamily: "Georgia, serif", color: INK }}><style>{printStyles}</style><div style={{ borderBottom: `2px solid ${GOLD}`, paddingBottom: 16, marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}><div><div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "0.1em" }}>YOMA DESIGN</div><div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: INK_SOFT }}>Coordination · Pilotage · Mandat</div></div><div style={{ textAlign: "right" }}><div style={{ fontSize: 13, fontWeight: 600 }}>{nomProjet || "Projet"}</div><div style={{ fontSize: 11, color: INK_SOFT }}>{today}</div><div style={{ fontSize: 11, color: INK_SOFT }}>{totalVillas} villas · {TYPE_PROJETS.find(t => t.value === typeProjet)?.label}</div>
          {deviseCode !== "MAD" && (
            <div style={{ fontSize: 11, color: GOLD, fontWeight: 600 }}>
              Devise : {deviseActive.symbol} {deviseCode} · 1 MAD = {tauxFinal} {deviseActive.symbol}
            </div>
          )}
        </div></div><div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: GOLD, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {printMode === "client" ? "Simulation de Rentabilité" : "Simulation Interne — Confidentiel"}
      </div><div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK_SOFT, marginBottom: 10 }}>Résultats par scénario</div><table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 28, fontSize: 12 }}><thead><tr style={{ background: SOFT_BG }}>
            {["Scénario", deviseCode !== "MAD" ? `CA (${deviseActive.symbol})` : "Chiffre d'affaires", "Frais notaire",
              ...(printMode === "complet" && afficherYoma ? ["Honoraires YOMA"] : []),
              "Coût total", "Bénéfice net", "Marge %", "Diagnostic"
            ].map((h, i) => (
              <th key={i} style={{ padding: "8px 10px", textAlign: i === 0 ? "left" : "right", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", borderBottom: `1px solid ${BORDER}` }}>{h}</th>
            ))}
          </tr></thead><tbody>
          {computed.map(sc => {
            const d = diagColor(sc.marge);
            return (
              <tr key={sc.id} style={{ borderBottom: `1px solid ${BORDER}` }}><td style={{ padding: "8px 10px", fontWeight: 700 }}>{sc.label}</td><td style={{ padding: "8px 10px", textAlign: "right" }}>{fmtDev(sc.ca)}{deviseCode !== "MAD" && <span style={{ fontSize: 10, color: "#999" }}> ({fmt(sc.ca)})</span>}</td><td style={{ padding: "8px 10px", textAlign: "right" }}>{fmtDev(sc.fraisNotaire)}</td>
                {printMode === "complet" && afficherYoma && (
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>{fmtDev(sc.honoraires)}</td>
                )}
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{fmtDev(sc.coutTotal)}</td><td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: sc.benefice >= 0 ? "#1A7A44" : "#C0392B" }}>{fmtDev(sc.benefice)}</td><td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700 }}>{pct(sc.marge)}</td><td style={{ padding: "8px 10px", textAlign: "right" }}><span style={{ background: d.bg, color: d.color, borderRadius: 4, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>{d.label}</span></td></tr>
            );
          })}
        </tbody></table>

      {printMode === "complet" && villas.some(v => v.type) && computed.map(sc => (
        <div key={sc.id} style={{ marginBottom: 20 }}><div style={{ fontSize: 11, fontWeight: 700, color: INK_SOFT, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>Détail par type — {sc.label}</div><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}><thead><tr style={{ background: SOFT_BG }}>
                {["Type", "Qté", "CA", "Coût constr.", "Bénéfice", "Marge %"].map((h, i) => (
                  <th key={i} style={{ padding: "6px 8px", textAlign: i <= 1 ? "left" : "right", fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: `1px solid ${BORDER}` }}>{h}</th>
                ))}
              </tr></thead><tbody>
              {sc.parType.map((v, i) => {
                const d = diagColor(v.margeV);
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${BORDER}` }}><td style={{ padding: "6px 8px", fontWeight: 600 }}>{v.type || `Type ${i + 1}`}</td><td style={{ padding: "6px 8px" }}>{v.qty}</td><td style={{ padding: "6px 8px", textAlign: "right" }}>{fmtDev(v.caV)}</td><td style={{ padding: "6px 8px", textAlign: "right" }}>{fmtDev(v.coutV)}</td><td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: v.benV >= 0 ? "#1A7A44" : "#C0392B" }}>{fmtDev(v.benV)}</td><td style={{ padding: "6px 8px", textAlign: "right" }}><span style={{ background: d.bg, color: d.color, borderRadius: 4, padding: "2px 6px", fontSize: 9, fontWeight: 700 }}>{pct(v.margeV)}</span></td></tr>
                );
              })}
            </tbody></table></div>
      ))}

      {printMode === "complet" && fraisEtudes && (
        <div style={{ marginTop: 24 }}><div style={{ fontSize: 12, fontWeight: 700, color: INK_SOFT, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>Estimation frais d'études & permis</div><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}><thead><tr style={{ background: SOFT_BG }}>
                {["Poste", "Min (MAD)", "Max (MAD)"].map((h, i) => (
                  <th key={i} style={{ padding: "6px 8px", textAlign: i === 0 ? "left" : "right", fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: `1px solid ${BORDER}` }}>{h}</th>
                ))}
              </tr></thead><tbody>
              {fraisEtudes.lignes.map((l, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${BORDER}` }}><td style={{ padding: "6px 8px" }}>{l.poste}</td><td style={{ padding: "6px 8px", textAlign: "right" }}>{fmt(l.min)}</td><td style={{ padding: "6px 8px", textAlign: "right" }}>{fmt(l.max)}</td></tr>
              ))}
              <tr style={{ background: SOFT_BG, fontWeight: 700 }}><td style={{ padding: "8px 8px" }}>TOTAL ESTIMÉ</td><td style={{ padding: "8px 8px", textAlign: "right", color: "#1A7A44" }}>{fmt(fraisEtudes.totalMin)}</td><td style={{ padding: "8px 8px", textAlign: "right", color: "#C0392B" }}>{fmt(fraisEtudes.totalMax)}</td></tr></tbody></table></div>
      )}

      <div style={{ borderTop: `1px solid ${BORDER}`, marginTop: 32, paddingTop: 12, fontSize: 10, color: INK_SOFT, display: "flex", justifyContent: "space-between" }}><span>YOMA DESIGN — Document confidentiel</span><span>{printMode === "client" ? "Version Client" : "Version Interne"} · {today}</span></div></div>
  );

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 1100 }}><style>{printStyles}</style><PrintZone />

      {/* En-tête */}
      <div style={{ marginBottom: 28 }}><div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, marginBottom: 4 }}>Décision — Simulation</div><div style={{ fontSize: 24, fontWeight: 700 }}>Simulation Multi-Scénarios</div><div style={{ fontSize: 13, color: INK_SOFT, marginTop: 3 }}>Rentabilité par type de villa · Frais notaire automatiques · 3 scénarios comparés</div></div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

        {/* Colonne gauche */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "22px 24px" }}><div style={sectionTitle}>Identification projet</div><div style={{ display: "flex", flexDirection: "column", gap: 12 }}><div><label style={labelStyle}>Nom du projet</label><input style={inputStyle} value={nomProjet} onChange={e => setNomProjet(e.target.value)} placeholder="Ex : Atlas Bay" /></div><div><label style={labelStyle}>Type de projet</label><select style={inputStyle} value={typeProjet} onChange={e => handleTypeProjet(e.target.value)}>
                  {TYPE_PROJETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select></div></div></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "22px 24px" }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}><div style={sectionTitle}>Types de villas</div><button onClick={addVilla} style={{ padding: "6px 14px", background: GOLD, color: WHITE, border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Ajouter</button></div><div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {villas.map((v, i) => (
                <div key={v.id} style={{ background: SOFT_BG, borderRadius: 8, padding: "14px 16px", border: `1px solid ${BORDER}` }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}><span style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: "0.1em", textTransform: "uppercase" }}>Type {i + 1}</span>
                    {villas.length > 1 && (
                      <button onClick={() => removeVilla(v.id)} style={{ background: "none", border: "none", color: "#C0392B", fontSize: 18, cursor: "pointer", padding: 0 }}>×</button>
                    )}
                  </div><div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8, marginBottom: 8 }}><div><label style={labelStyle}>Nom du type</label><input style={inputStyle} value={v.type} onChange={e => updateVilla(v.id, "type", e.target.value)} placeholder="Ex : Villa ARIA" /></div><div><label style={labelStyle}>Quantité</label><input style={inputStyle} type="number" value={v.quantite} onChange={e => updateVilla(v.id, "quantite", e.target.value)} placeholder="8" /></div></div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}><div><label style={labelStyle}>Prix vente unitaire (MAD)</label><input style={inputStyle} type="number" value={v.prixUnitaire} onChange={e => updateVilla(v.id, "prixUnitaire", e.target.value)} placeholder="3 850 000" /></div><div><label style={labelStyle}>Coût construction unitaire (MAD)</label><input style={inputStyle} type="number" value={v.coutConstruction} onChange={e => updateVilla(v.id, "coutConstruction", e.target.value)} placeholder="2 200 000" /></div></div></div>
              ))}
            </div></div></div>

        {/* Colonne droite */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "22px 24px" }}><div style={sectionTitle}>Coûts du projet</div><div style={{ display: "flex", flexDirection: "column", gap: 12 }}><div><label style={labelStyle}>Coût terrain global (MAD)</label><input style={inputStyle} type="number" value={coutTerrain} onChange={e => setCoutTerrain(e.target.value)} placeholder="2 000 000" /></div><div style={{ background: SOFT_BG, borderRadius: 8, padding: "14px 16px", border: `1px solid ${BORDER}` }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}><label style={{ ...labelStyle, marginBottom: 0 }}>Frais de notaire (%)</label><span style={{ fontSize: 11, color: GOLD, fontWeight: 600 }}>Maroc : 6% – 8%</span></div><input style={inputStyle} type="number" step="0.1" value={fraisNotairePct} onChange={e => setFraisNotairePct(e.target.value)} placeholder="7" /><div style={{ fontSize: 11, color: INK_SOFT, marginTop: 6, fontStyle: "italic" }}>
                  Enregistrement ~4% · Conservation ~1.5% · Honoraires notaire ~1.5%
                </div></div><div><label style={labelStyle}>Frais divers (permis, études…) (MAD)</label><input style={inputStyle} type="number" value={fraisDivers} onChange={e => setFraisDivers(e.target.value)} placeholder="300 000" />
                {fraisEtudes && (
                  <div style={{ marginTop: 6, fontSize: 11, color: GOLD, fontStyle: "italic", cursor: "pointer" }}
                    onClick={() => setFraisDivers(String(Math.round(fraisEtudes.moyenne)))}>
                     Estimation auto : {fmt(fraisEtudes.totalMin)} – {fmt(fraisEtudes.totalMax)} → <strong>Appliquer la moyenne</strong></div>
                )}
              </div><div style={{ background: "#FDF9F2", borderRadius: 8, padding: "14px 16px", border: `1px solid #F0DEB4` }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}><label style={{ ...labelStyle, marginBottom: 0, color: GOLD }}>Honoraires YOMA</label><span style={{ fontSize: 11, color: GOLD, fontWeight: 600 }}>{TYPE_PROJETS.find(t => t.value === typeProjet)?.label}</span></div><div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><input type="checkbox" id="inclureYoma" checked={inclureYoma} onChange={e => setInclureYoma(e.target.checked)} style={{ width: 15, height: 15, cursor: "pointer", accentColor: GOLD }} /><label htmlFor="inclureYoma" style={{ fontSize: 12, color: INK, cursor: "pointer" }}>Inclure dans le calcul</label></div><div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><input type="checkbox" id="afficherYoma" checked={afficherYoma} onChange={e => setAfficherYoma(e.target.checked)} style={{ width: 15, height: 15, cursor: "pointer", accentColor: GOLD }} /><label htmlFor="afficherYoma" style={{ fontSize: 12, color: INK, cursor: "pointer" }}>Afficher dans la version complète imprimée</label></div><input style={{ ...inputStyle, borderColor: "#F0DEB4", opacity: inclureYoma ? 1 : 0.4 }}
                  type="number" value={forfaitYoma} disabled={!inclureYoma} onChange={e => setForfaitYoma(e.target.value)} /></div></div></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "22px 24px" }}><div style={sectionTitle}>Réglages scénarios</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {scenarios.map(sc => (
                <div key={sc.id} style={{ background: SOFT_BG, borderRadius: 8, padding: "12px 14px", border: `1px solid ${BORDER}` }}><div style={{ fontSize: 12, fontWeight: 700, color: INK, marginBottom: 10 }}>{sc.label}</div><div style={{ marginBottom: 8 }}><label style={labelStyle}>Prix vente (%)</label><input style={{ ...inputStyle, padding: "6px 8px" }} type="number" value={sc.pctVente} onChange={e => updateScenario(sc.id, "pctVente", e.target.value)} /></div><div><label style={labelStyle}>Coût constr. (%)</label><input style={{ ...inputStyle, padding: "6px 8px" }} type="number" value={sc.pctCout} onChange={e => updateScenario(sc.id, "pctCout", e.target.value)} /></div></div>
              ))}
            </div><div style={{ fontSize: 11, color: INK_SOFT, marginTop: 10, fontStyle: "italic" }}>
              Prudent = prix −10% / coût +10% · Ambitieux = prix +10% / coût −5%
            </div></div></div></div>

      {/* ── Convertisseur de devises ── */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 24px", marginBottom: 20 }}><div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}><div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: GOLD, fontWeight: 700, whiteSpace: "nowrap" }}>
             Affichage devise
          </div><div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {DEVISES.map(d => (
              <button key={d.code} onClick={() => { setDeviseCode(d.code); setTauxManuel(""); }}
                style={{
                  padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                  fontWeight: deviseCode === d.code ? 700 : 400,
                  background: deviseCode === d.code ? INK : SOFT_BG,
                  color: deviseCode === d.code ? WHITE : INK_SOFT,
                  border: `1px solid ${deviseCode === d.code ? INK : BORDER}`,
                  transition: "all 0.15s",
                }}>
                {d.symbol} {d.code}
              </button>
            ))}
          </div>
          {deviseCode !== "MAD" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}><span style={{ fontSize: 11, color: INK_SOFT, whiteSpace: "nowrap" }}>1 MAD =</span><input
                style={{ ...inputStyle, width: 90, padding: "6px 8px" }}
                type="number" step="0.001"
                value={tauxManuel || deviseActive.taux}
                onChange={e => setTauxManuel(e.target.value)}
              /><span style={{ fontSize: 11, color: INK_SOFT }}>{deviseActive.symbol}</span><span style={{ fontSize: 10, color: "#bbb", fontStyle: "italic" }}>(modifiable)</span></div>
          )}
        </div></div>

      {/* Résultats */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "24px 28px", marginBottom: 20 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}><div style={sectionTitle}>
            Résultats — {nomProjet || "Projet"} · {totalVillas} villas
            {deviseCode !== "MAD" && <span style={{ color: INK_SOFT, marginLeft: 8 }}>· {deviseActive.symbol} {deviseCode}</span>}
          </div><div className="no-print" style={{ display: "flex", gap: 10 }}><button onClick={() => handlePrint("complet")} style={{ padding: "8px 18px", background: INK, color: WHITE, border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
               Version complète
            </button><button onClick={() => handlePrint("client")} style={{ padding: "8px 18px", background: GOLD, color: WHITE, border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
               Version client
            </button></div></div><table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr style={{ borderBottom: `2px solid ${BORDER}` }}>
              {["Scénario", "Chiffre d'affaires", "Frais notaire", "Coût total", "Bénéfice net", "Marge %", "Diagnostic"].map((h, i) => (
                <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "8px 10px", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: INK_SOFT, fontWeight: 600 }}>{h}</th>
              ))}
            </tr></thead><tbody>
            {computed.map(sc => {
              const d = diagColor(sc.marge);
              return (
                <tr key={sc.id} style={{ borderBottom: `1px solid ${SOFT_BG}` }}><td style={{ padding: "12px 10px", fontWeight: 700, fontSize: 14 }}>{sc.label}</td><td style={{ padding: "12px 10px", textAlign: "right", fontSize: 13 }}>{fmtBoth(sc.ca)}</td><td style={{ padding: "12px 10px", textAlign: "right", fontSize: 13, color: "#C0392B" }}>{fmtBoth(sc.fraisNotaire)}</td><td style={{ padding: "12px 10px", textAlign: "right", fontSize: 13 }}>{fmtBoth(sc.coutTotal)}</td><td style={{ padding: "12px 10px", textAlign: "right", fontSize: 13, fontWeight: 700, color: sc.benefice >= 0 ? "#1A7A44" : "#C0392B" }}>{fmtBoth(sc.benefice)}</td><td style={{ padding: "12px 10px", textAlign: "right", fontSize: 13, fontWeight: 700 }}>{pct(sc.marge)}</td><td style={{ padding: "12px 10px", textAlign: "right" }}><span style={{ background: d.bg, color: d.color, borderRadius: 999, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{d.label}</span></td></tr>
              );
            })}
          </tbody></table></div>

      {/* Rentabilité par type */}
      {villas.some(v => v.type) && (
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "24px 28px", marginBottom: 20 }}><div style={sectionTitle}>Rentabilité par type de villa</div>
          {computed.map(sc => (
            <div key={sc.id} style={{ marginBottom: 20 }}><div style={{ fontSize: 12, fontWeight: 700, color: INK_SOFT, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>Scénario {sc.label}</div><table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                    {["Type", "Qté", "CA", "Coût constr.", "Bénéfice", "Marge %"].map((h, i) => (
                      <th key={i} style={{ textAlign: i <= 1 ? "left" : "right", padding: "6px 10px", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: INK_SOFT, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr></thead><tbody>
                  {sc.parType.map((v, i) => {
                    const d = diagColor(v.margeV);
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${SOFT_BG}` }}><td style={{ padding: "8px 10px", fontSize: 13, fontWeight: 600 }}>{v.type || `Type ${i + 1}`}</td><td style={{ padding: "8px 10px", fontSize: 13 }}>{v.qty}</td><td style={{ padding: "8px 10px", textAlign: "right", fontSize: 12 }}>{fmtBoth(v.caV)}</td><td style={{ padding: "8px 10px", textAlign: "right", fontSize: 12 }}>{fmtBoth(v.coutV)}</td><td style={{ padding: "8px 10px", textAlign: "right", fontSize: 12, fontWeight: 700, color: v.benV >= 0 ? "#1A7A44" : "#C0392B" }}>{fmtBoth(v.benV)}</td><td style={{ padding: "8px 10px", textAlign: "right" }}><span style={{ background: d.bg, color: d.color, borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 700 }}>{pct(v.margeV)}</span></td></tr>
                    );
                  })}
                </tbody></table></div>
          ))}
        </div>
      )}

      {/* Frais d'Études & Permis */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "24px 28px" }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}><div style={sectionTitle}>Estimation Frais d'Études & Permis</div><button onClick={() => setAfficherEtudes(v => !v)} style={{
            padding: "6px 16px", background: afficherEtudes ? INK : SOFT_BG,
            color: afficherEtudes ? WHITE : INK_SOFT,
            border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>
            {afficherEtudes ? "Masquer" : "Calculer"}
          </button></div>

        {afficherEtudes && (
          <><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}><div><label style={labelStyle}>Surface totale construite (m²)</label><input style={inputStyle} type="number" value={surfaceEtudes} onChange={e => setSurfaceEtudes(e.target.value)} placeholder="Ex : 347" /></div><div><label style={labelStyle}>Nombre d'étages (R+…)</label><input style={inputStyle} type="number" value={nbrEtages} onChange={e => setNbrEtages(e.target.value)} placeholder="1" /></div><div><label style={labelStyle}>Type de structure</label><select style={inputStyle} value={typeStructure} onChange={e => setTypeStructure(e.target.value)}><option value="villa">Villa / Maison individuelle</option><option value="immeuble">Immeuble / Collectif</option><option value="lotissement">Lotissement / Promotion</option></select></div></div>

            {fraisEtudes ? (
              <><table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}><thead><tr style={{ borderBottom: `2px solid ${BORDER}` }}>
                      {["Poste de dépense", "Minimum (MAD)", "Maximum (MAD)"].map((h, i) => (
                        <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "8px 10px", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: INK_SOFT, fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr></thead><tbody>
                    {fraisEtudes.lignes.map((l, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${SOFT_BG}` }}><td style={{ padding: "10px 10px", fontSize: 13 }}>{l.poste}</td><td style={{ padding: "10px 10px", textAlign: "right", fontSize: 13, color: "#1A7A44" }}>{fmt(l.min)}</td><td style={{ padding: "10px 10px", textAlign: "right", fontSize: 13, color: "#C0392B" }}>{fmt(l.max)}</td></tr>
                    ))}
                    <tr style={{ background: SOFT_BG, borderTop: `2px solid ${BORDER}` }}><td style={{ padding: "12px 10px", fontWeight: 700, fontSize: 14 }}>TOTAL ESTIMÉ</td><td style={{ padding: "12px 10px", textAlign: "right", fontWeight: 700, fontSize: 14, color: "#1A7A44" }}>{fmt(fraisEtudes.totalMin)}</td><td style={{ padding: "12px 10px", textAlign: "right", fontWeight: 700, fontSize: 14, color: "#C0392B" }}>{fmt(fraisEtudes.totalMax)}</td></tr></tbody></table><div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "#FDF9F2", borderRadius: 8, border: `1px solid #F0DEB4` }}><div style={{ flex: 1, fontSize: 13, color: INK }}>
                    Moyenne estimée : <strong style={{ color: GOLD }}>{fmt(fraisEtudes.moyenne)}</strong></div><button onClick={() => setFraisDivers(String(Math.round(fraisEtudes.moyenne)))} style={{
                    padding: "8px 18px", background: GOLD, color: WHITE,
                    border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  }}>
                    ↑ Appliquer dans la simulation
                  </button></div></>
            ) : (
              <div style={{ padding: "24px 0", textAlign: "center", color: "#bbb", fontSize: 13, fontStyle: "italic" }}>
                Saisissez la surface et le nombre d'étages pour obtenir l'estimation
              </div>
            )}
          </>
        )}
      </div></div>
  );
}
